import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import { Component, computed, inject, Injector, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  OnyxBadgeComponent,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxToastService,
} from '@onyx/angular';
import { intersectionBy, union } from 'lodash';
import { Subject } from 'rxjs';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { FleetIdentifierPipe } from '../../../../../common/pipes/fleet-identifier.pipe';
import { EmployeeHelper } from '../../../../management-panel/employees/common/helpers/employee.helper';
import type { Employee } from '../../../../management-panel/employees/common/interfaces/employee';
import type { Fleet } from '../../interfaces/fleet';
import { FleetService } from '../../services/fleet.service';

export type FleetAssignedEmployeesModalData = Pick<
  Fleet,
  'uuid' | 'generalInformation' | 'assignedEmployees'
>[];

@Component({
  selector: 'app-fleet-assigned-employees-modal',
  imports: [
    OnyxModalComponent,
    OnyxDropdownComponent,
    ReactiveFormsModule,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxBadgeComponent,
    OnyxIconComponent,
    FleetIdentifierPipe,
    OnyxIconComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './fleet-assigned-employees-modal.component.html',
  styleUrl: './fleet-assigned-employees-modal.component.scss',
})
export class FleetAssignedEmployeesModalComponent implements OnInit {
  protected readonly vehicles =
    inject<FleetAssignedEmployeesModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly fleetService = inject(FleetService);
  private readonly translateService = inject(TranslateService);
  private readonly employeeHelper = inject(EmployeeHelper);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'fleet.assignedEmployeesModal';
  protected readonly EMPLOYEES_SOURCE =
    this.employeeHelper.getEmployeesSource();

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  protected isSingle = computed(() => this.vehicles.length === 1);

  public ngOnInit(): void {
    if (this.vehicles.length > 1) {
      const allEmployees = this.vehicles
        .filter(({ assignedEmployees }) => assignedEmployees?.length)
        .flatMap(({ assignedEmployees }) => assignedEmployees);
      const commonEmployees = intersectionBy(allEmployees, 'uuid');
      const selectedEmployees =
        commonEmployees.length > 0 ? commonEmployees : union(allEmployees);

      this.form.controls.employees.setValue(
        selectedEmployees.map((employee) => employee!.uuid),
      );

      this.toastService.showInformation({
        message:
          commonEmployees.length > 0
            ? `${this.I18N}.commonEmployees`
            : `${this.I18N}.allEmployees`,
      });
    } else if (this.vehicles[0]?.assignedEmployees) {
      const uuids = this.vehicles[0]?.assignedEmployees.map(
        (employee) => employee.uuid,
      );
      this.form.controls.employees.setValue(uuids);
    }
  }

  protected openModal(employee: Employee | null): void {
    if (employee) this.employeeHelper.openModal(employee);
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const { employees } = this.form.getRawValue();

    const action$ = this.isSingle()
      ? this.fleetService.updateAssignedEmployees(
          this.vehicles[0].uuid,
          employees,
        )
      : this.fleetService.batchFleet(
          this.vehicles.map((vehicle) => vehicle.uuid),
          { assignedEmployees: employees },
        );

    this.loading.set(true);
    action$
      .subscribe(() => {
        this.toastService.showSuccess({
          message: this.translateService.instant(
            `${this.I18N}.employeesAssigned`,
            { value: employees.length },
          ),
        });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      employees: this.fb.control<string[]>([]),
    });
  }
}
